<template>
    <div id="productCustomExcelModal" class="modal centered-modal">
        <div class="modal-dialog max-w-xl my-auto rounded-xl shadow-lg">
            <div class="modal-header">
                <div class="flex items-center gap-2">
                    <span class="w-12 h-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center text-base">
                        <i class="fa-solid fa-file-excel"></i>
                    </span>
                    <div>
                        <h3 class="modal-title font-semibold text-lg text-slate-800">
                            {{ $t('label.custom_excel_tool') }}
                        </h3>
                        <p class="text-xs text-slate-500 leading-none">
                            {{ $t('label.custom_excel_desc') }}
                        </p>
                    </div>
                </div>
                <button type="button" class="modal-close fa-solid fa-xmark text-xl text-slate-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed" :disabled="isDownloading || isUploading" @click="reset"></button>
            </div>

            <div class="modal-body p-5">
                <!-- TABS: EXPORTAR / RE-IMPORTAR -->
                <div class="flex border-b border-slate-200 mb-4">
                    <button
                        type="button"
                        @click="activeTab = 'export'"
                        class="pb-2.5 px-4 font-semibold text-sm transition border-b-2 flex items-center gap-2"
                        :class="activeTab === 'export' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'"
                    >
                        <i class="fa-solid fa-cloud-arrow-down"></i>
                        <span>1. {{ $t('button.export') }}</span>
                    </button>
                    <button
                        type="button"
                        @click="activeTab = 'import'"
                        class="pb-2.5 px-4 font-semibold text-sm transition border-b-2 flex items-center gap-2"
                        :class="activeTab === 'import' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'"
                    >
                        <i class="fa-solid fa-cloud-arrow-up"></i>
                        <span>2. {{ $t('button.upload_file') || $t('label.upload_file') || 'Subir' }}</span>
                    </button>
                </div>

                <!-- PESTAÑA 1: EXPORTACIÓN A MEDIDA -->
                <div v-if="activeTab === 'export'">
                    <div class="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200 mb-4 max-h-56 overflow-y-auto">
                        <label
                            v-for="(info, key) in allowedFields"
                            :key="key"
                            class="flex items-center gap-2 p-2 rounded-lg bg-gray-100 cursor-pointer hover:border-primary/40 transition text-xs font-medium text-slate-700 select-none"
                        >
                            <input
                                type="checkbox"
                                :value="key"
                                v-model="selectedColumns"
                                class="rounded text-primary focus:ring-primary w-4 h-4 cursor-pointer"
                            />
                            <span>{{ info.label }}</span>
                        </label>
                    </div>

                    <div class="flex items-center justify-between text-xs text-slate-500 mb-4">
                        <span>{{ selectedColumns.length }} {{ $t('label.columns') || 'columnas' }}</span>
                        <div class="flex items-center gap-1 text-xs">
                            <button type="button" @click="selectAllColumns" class="text-primary hover:underline">{{ $t('label.all') || 'Todas' }}</button>
                            <span>|</span>
                            <button type="button" @click="selectedColumns = ['selling_price', 'stock']" class="text-slate-500 hover:underline">{{ $t('label.only_price_and_stock') || 'Solo precio y stock' }}</button>
                        </div>
                    </div>

                    <div class="modal-btns pt-3 border-t border-slate-200 flex justify-end gap-2">
                        <button type="button" class="modal-btn-outline modal-close py-2 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-medium" :disabled="isDownloading" @click="reset">
                            <span>{{ $t('button.cancel') }}</span>
                        </button>
                        <button
                            type="button"
                            @click="downloadCustomExcel"
                            class="db-btn py-2 px-5 text-white bg-primary rounded-lg text-sm font-medium shadow hover:opacity-90 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            :disabled="selectedColumns.length === 0 || isDownloading"
                        >
                            <template v-if="isDownloading">
                                <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('label.processing') || 'Procesando...' }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-file-arrow-down"></i>
                                <span>{{ $t('button.download_custom_excel') }}</span>
                            </template>
                        </button>
                    </div>
                </div>

                <!-- PESTAÑA 2: RE-IMPORTACIÓN -->
                <div v-else-if="activeTab === 'import'">
                    <div class="mb-4">
                        <label class="db-field-title required font-semibold text-xs uppercase tracking-wider text-slate-600 mb-1.5 block">
                            {{ $t('label.upload_file') }} (.xlsx, .xls)
                        </label>
                        <input
                            type="file"
                            ref="excelFileInput"
                            @change="onFileSelected"
                            :disabled="isUploading"
                            accept=".xlsx, .xls, .csv"
                            class="db-field-control w-full bg-slate-50 border border-slate-300 rounded-lg py-0 px-3 text-xs"
                        />
                    </div>

                    <div class="modal-btns pt-3 border-t border-slate-200 flex justify-end gap-2">
                        <button type="button" class="modal-btn-outline modal-close py-2 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-medium" :disabled="isUploading" @click="reset">
                            <span>{{ $t('button.cancel') }}</span>
                        </button>
                        <button
                            type="button"
                            @click="uploadCustomExcel"
                            class="db-btn py-2 px-5 text-white bg-primary rounded-lg text-sm font-medium shadow hover:opacity-90 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            :disabled="!selectedFile || isUploading"
                        >
                            <template v-if="isUploading">
                                <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('label.processing') || 'Procesando...' }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-cloud-arrow-up"></i>
                                <span>{{ $t('button.upload_custom_excel') }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import appService from "../../../services/appService";

export default {
    name: "ProductCustomExcelModal",
    props: {
        searchFilters: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            activeTab: "export",
            selectedColumns: ["selling_price", "buying_price", "stock", "status"],
            selectedFile: null,
            isDownloading: false,
            isUploading: false
        };
    },
    computed: {
        allowedFields() {
            const storeFields = this.$store.getters['bulkUpdate/allowedFields'];
            if (storeFields && Object.keys(storeFields).length > 0) {
                return storeFields;
            }
            // Fallback inmediato garantizado
            return {
                selling_price: { label: this.$t('label.selling_price') || "Precio de Venta", type: "numeric" },
                buying_price: { label: this.$t('label.buying_price') || "Precio de Compra", type: "numeric" },
                wholesale_price: { label: this.$t('label.wholesale_price') || "Precio de Mayoreo", type: "numeric" },
                stock: { label: this.$t('label.stock') || "Stock Disponible", type: "numeric" },
                weight: { label: this.$t('label.weight_kg') || "Peso (kg)", type: "numeric" },
                length: { label: this.$t('label.length_cm') || "Largo (cm)", type: "numeric" },
                width: { label: this.$t('label.width_cm') || "Ancho (cm)", type: "numeric" },
                height: { label: this.$t('label.height_cm') || "Alto (cm)", type: "numeric" },
                status: { label: this.$t('label.product_status') || "Estado del Producto", type: "enum_status" },
                can_purchasable: { label: this.$t('label.can_purchasable') || "Comprable", type: "enum_ask" },
                show_stock_out: { label: this.$t('label.show_stock_out') || "Mostrar si Agotado", type: "enum_ask" },
                refundable: { label: this.$t('label.refundable') || "Reembolsable", type: "enum_ask" },
                maximum_purchase_quantity: { label: this.$t('label.maximum_purchase_quantity') || "Límite Máximo de Compra", type: "numeric" },
                low_stock_quantity_warning: { label: this.$t('label.low_stock_quantity_warning') || "Alerta de Stock Bajo", type: "numeric" },
                warranty: { label: this.$t('label.warranty') || "Garantía", type: "text" }
            };
        }
    },
    mounted() {
        this.$store.dispatch('bulkUpdate/getOptions').catch(() => {});
    },
    methods: {
        reset() {
            if (this.isDownloading || this.isUploading) return;
            appService.modalHide();
            this.selectedFile = null;
            if (this.$refs.excelFileInput) {
                this.$refs.excelFileInput.value = null;
            }
        },
        selectAllColumns() {
            if (this.isDownloading) return;
            this.selectedColumns = Object.keys(this.allowedFields);
        },
        onFileSelected(e) {
            this.selectedFile = e.target.files[0] || null;
        },
        downloadCustomExcel() {
            if (this.isDownloading) return;
            this.isDownloading = true;
            this.$emit('export-custom', {
                filters: this.searchFilters,
                columns: this.selectedColumns,
                onDone: () => {
                    this.isDownloading = false;
                }
            });
        },
        uploadCustomExcel() {
            if (!this.selectedFile || this.isUploading) return;
            this.isUploading = true;
            const fd = new FormData();
            fd.append('file', this.selectedFile);
            this.$emit('import-custom', {
                formData: fd,
                onDone: () => {
                    this.isUploading = false;
                    this.reset();
                }
            });
        }
    }
};
</script>

<style scoped>
.centered-modal {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 1rem;
}

.centered-modal .modal-dialog {
    margin: auto !important;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.centered-modal .modal-body {
    overflow-y: auto;
}
</style>
